import {ApolloError} from '@apollo/client';

import {AdcConjugationMethod} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  CONJUGATION_SITES_LOOKUP_ERROR,
  ConjugationSite,
} from 'molbio/adcs/conjugation-sites/AdcConjugationSiteConstants';
import {conjugationSiteKey} from 'molbio/adcs/conjugation-sites/AdcConjugationSiteUtils';
import {AntibodyDomain, fetchAntibodyDomains} from 'molbio/adcs/conjugation-sites/antibodyDomainsQuery';

export interface FindConjugationSiteCandidatesInput {
  antibodyId: string;
  conjugationMethod: AdcConjugationMethod;
}

export type ConjugationSiteCandidatesResult =
  | {supported: true; candidates: Array<ConjugationSite>}
  | {supported: false}
  /** The candidates couldn't be looked up, e.g. because the antibody's domains failed to load. */
  | {error: string};

/**
 * The IgG1 sites that engineered cysteine conjugation can use: EU C118 in CH1 (from A118C) and EU C239 in CH2
 * (from S239C). An antibody has only the ones it was engineered with.
 */
export const ENGINEERED_CYSTEINE_SITES: ReadonlyArray<ConjugationSite> = [
  {domainLabel: 'CH1(Heavy)', residueIndex: 0},
  {domainLabel: 'CH2(Heavy)', residueIndex: 8},
];

/**
 * The IgG1 sites that disulfide reduction can use: the interchain cysteines EU C220, C226, and C229 in the
 * hinge and EU C214 in CL.
 */
export const DISULFIDE_REDUCTION_SITES: ReadonlyArray<ConjugationSite> = [
  {domainLabel: 'H(Heavy)', residueIndex: 4},
  {domainLabel: 'H(Heavy)', residueIndex: 10},
  {domainLabel: 'H(Heavy)', residueIndex: 13},
  {domainLabel: 'CL(Light)', residueIndex: 106},
];

const AVAILABLE_SITES_BY_METHOD: Partial<Record<AdcConjugationMethod, ReadonlyArray<ConjugationSite>>> = {
  [AdcConjugationMethod.ENGINEERED_CYSTEINE]: ENGINEERED_CYSTEINE_SITES,
  [AdcConjugationMethod.DISULFIDE_CONJUGATION]: DISULFIDE_REDUCTION_SITES,
};

/**
 * Finds the cysteine (C) residues in the given antibody domains that are among the given available sites,
 * skipping domains whose sequence is inaccessible.
 *
 * @param availableSites The sites the conjugation method can use, if they are cysteines
 */
export function findCysteineConjugationSites(
  domains: ReadonlyArray<AntibodyDomain>,
  availableSites: ReadonlyArray<ConjugationSite>
): Array<ConjugationSite> {
  const availableKeys = new Set(availableSites.map((site) => conjugationSiteKey(site)));
  const sites: Array<ConjugationSite> = [];
  for (const domain of domains) {
    if (domain.entity.__typename === 'InaccessibleInfoNode') {
      continue;
    }
    const residues = domain.entity.experimentalCurrentVersion.aminoAcids;
    for (let residueIndex = 0; residueIndex < residues.length; residueIndex++) {
      const site = {domainLabel: domain.label, residueIndex};
      if (residues[residueIndex].toUpperCase() === 'C' && availableKeys.has(conjugationSiteKey(site))) {
        sites.push(site);
      }
    }
  }
  return sites;
}

/**
 * Returns the residues on an antibody where a payload could attach using the given conjugation method.
 *
 * Engineered cysteine and disulfide conjugation attach payloads to cysteines, so their candidates are the
 * antibody's cysteines at the sites each method can use (ENGINEERED_CYSTEINE_SITES and
 * DISULFIDE_REDUCTION_SITES). An inaccessible antibody has no candidates. Every other method is reported as
 * unsupported.
 */
export async function findConjugationSiteCandidates({
  antibodyId,
  conjugationMethod,
}: FindConjugationSiteCandidatesInput): Promise<ConjugationSiteCandidatesResult> {
  const availableSites = AVAILABLE_SITES_BY_METHOD[conjugationMethod];
  if (availableSites == null) {
    return {supported: false};
  }
  let domains: Array<AntibodyDomain> | null;
  try {
    domains = await fetchAntibodyDomains(antibodyId);
  } catch (error) {
    if (error instanceof ApolloError) {
      return {error: CONJUGATION_SITES_LOOKUP_ERROR};
    }
    throw error;
  }
  return {
    supported: true,
    candidates: domains != null ? findCysteineConjugationSites(domains, availableSites) : [],
  };
}
